/* eslint-disable @typescript-eslint/indent */
import { Injectable } from "@angular/core";
import { Validators } from "@angular/forms";
import { Observable, of } from "rxjs";
import { map } from "rxjs/operators";
import { QuestionBase } from "src/app/shared/components/dynamic-form/question-base";
import { DropdownOption, DropdownQuestion } from "src/app/shared/components/dynamic-form/question-dropdown";
import { NumberQuestion } from "src/app/shared/components/dynamic-form/question-number";
import { pattern } from "src/app/shared/constants/patterns";
import { assetAttributeTypes } from "./asset";
import { AssetService } from "./asset.service";
import { AssetServiceUI } from "./asset.service.ui";
import { Asset, AssetAttribute } from "./asset.type";
import { setFilterCondition } from "../../../shared/utils/helpers/common.helper";

@Injectable()
export class AssetAttributesService {
  constructor(private assetService: AssetService, private assetServiceUI: AssetServiceUI) {}

  getAttributes(
    query?: { type?: string; name?: string },
    assetAttributes?: AssetAttribute[],
  ): Observable<DropdownOption[]> {
    const { type, ...filters } = query;
    return this.assetService
      .getAttributes({
        where: { type, ...setFilterCondition(filters, "$cont") },
      })
      .pipe(
        map((r) =>
          r.items.map((attribute) => {
            return {
              value: attribute.id,
              label: attribute.name,
              disabled: !!assetAttributes?.find(
                (assetAttribute) => assetAttribute.attributeId === attribute.id && !assetAttribute.deleted,
              ),
            };
          }),
        ),
      );
  }

  generateQuestions(options?: {
    assetAttributes?: AssetAttribute[];
    asset: Asset;
  }): Observable<QuestionBase<string | boolean | number>[]> {
    const questions: QuestionBase<string | boolean | number>[] = [
      new DropdownQuestion({
        key: "type",
        label: "Attribute Type",
        required: true,
        options: assetAttributeTypes.filter((type) => type.auxValue === options.asset.category),
        order: 1,
      }),
      new DropdownQuestion({
        key: "attributeId",
        label: "Attribute Name",
        required: true,
        order: 2,
        dependsOn: {
          key: "type",
          callback: ({ parentValue: type }) => {
            return this.getAttributes({ type: type }, options?.assetAttributes);
          },
        },
        search: {
          filter: {
            parentKey: "type",
            callback: ({ parentValue: type, value: name }) =>
              this.getAttributes({ type, name }, options?.assetAttributes),
          },
        },
        addNewEntry: {
          action: (form, dropdown) => {
            const type = assetAttributeTypes.find((t) => t.value === form.value.type);
            const newEntry = this.assetServiceUI.createAttributeModal(this.assetService, type);
            newEntry.afterClosed().subscribe((data) => {
              if (data) {
                dropdown.options = [...dropdown.options, { value: data.id, label: data.name }];
                form.controls["attributeId"].setValue(data.id);
                form.controls["attributeId"].markAsDirty();
              }
            });
          },
        },
      }),

      new NumberQuestion({
        key: "quantity",
        label: " Quantity",
        required: true,
        order: 3,
        errors: [
          {
            key: "pattern",
            validator: Validators.pattern(pattern.price),
            message: "Quantity is <strong>not valid</strong>",
          },
        ],
      }),
    ];

    return of(questions.filter((field) => field).sort((a, b) => a.order - b.order));
  }
}
